// Pasul 54.5: aprecierile (Like) pe lucrari, expozitii si membri.
// Tipurile de tinta urmeaza caile API-ului: /public/{lucrari|expozitii|membri}/{id}/like.
import {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {API_ENDPOINT} from '../config';

export const TIPURI_APRECIERE = ['lucrari', 'expozitii', 'membri'];
// Acelasi plafon ca pe server (ContentLikeController::MAX_IDS_ALE_MELE).
export const MAX_IDS_PER_CERERE = 100;

// Textul butonului, dupa gramatica romaneasca: „de” la 20 si peste (cu ultimele doua cifre 00 sau 20–99).
export const textAprecieri = (n) => {
    const numar = Math.max(0, Number(n) || 0);
    if (numar === 0) return 'Apreciază';
    if (numar === 1) return '1 apreciere';
    const rest = numar % 100;
    return numar >= 20 && (rest === 0 || rest >= 20) ? `${numar} de aprecieri` : `${numar} aprecieri`;
};

const tokenCont = () => {
    try {
        return localStorage.getItem('authToken');
    } catch {
        return null;
    }
};

// Cookie-ul anonim (HttpOnly) circula doar cu withCredentials; daca e cineva logat, JWT-ul are prioritate.
// Un token expirat nu trebuie sa blocheze aprecierea: la 401 reincercam o data ca vizitator.
export const cerereApreciere = async (config) => {
    const token = tokenCont();
    const baza = {...config, withCredentials: true};
    if (!token) return axios(baza);
    try {
        return await axios({...baza, headers: {...(config.headers || {}), Authorization: `Bearer ${token}`}});
    } catch (e) {
        if (e?.response?.status === 401) return axios(baza);
        throw e;
    }
};

export const schimbaApreciere = (tip, id, apreciaza) => cerereApreciere({
    method: apreciaza ? 'post' : 'delete',
    url: `${API_ENDPOINT}/public/${tip}/${id}/like`,
}).then((r) => r.data);

const idsValide = (ids) => [...new Set((ids || []).filter((id) => Number.isInteger(id) && id > 0))];

// Imparte cererea in loturi de cel mult MAX_IDS_PER_CERERE id-uri per tip (o expozitie poate avea mai multe lucrari).
export const loturiAleMele = (cereri) => {
    const liste = Object.fromEntries(TIPURI_APRECIERE.map((tip) => [tip, idsValide(cereri?.[tip])]));
    const nrLoturi = Math.max(0, ...TIPURI_APRECIERE.map((tip) => Math.ceil(liste[tip].length / MAX_IDS_PER_CERERE)));
    return Array.from({length: nrLoturi}, (_, i) => Object.fromEntries(TIPURI_APRECIERE
        .map((tip) => [tip, liste[tip].slice(i * MAX_IDS_PER_CERERE, (i + 1) * MAX_IDS_PER_CERERE).join(',')])
        .filter(([, valoare]) => valoare !== '')));
};

// Starea „apreciat de mine” pentru o pagina intreaga, intr-o singura cerere (sau cateva loturi):
// intoarce o functie (tip, id) → true / false, sau undefined cat timp raspunsul nu a sosit.
export const useAprecieriMele = (cereri) => {
    const loturi = loturiAleMele(cereri);
    const cheie = JSON.stringify(loturi);
    const [mele, setMele] = useState(null);

    useEffect(() => {
        const parametri = JSON.parse(cheie);
        if (parametri.length === 0) {
            setMele(null);
            return undefined;
        }
        const controller = new AbortController();
        Promise.all(parametri.map((params) => cerereApreciere({
            method: 'get', url: `${API_ENDPOINT}/public/likes/ale-mele`, params, signal: controller.signal,
        })))
            .then((raspunsuri) => setMele(Object.fromEntries(TIPURI_APRECIERE.map((tip) => [
                tip, new Set(raspunsuri.flatMap((r) => r.data?.[tip] || [])),
            ]))))
            .catch(() => {});
        return () => controller.abort();
    }, [cheie]);

    return useCallback((tip, id) => (mele ? mele[tip]?.has(id) ?? false : undefined), [mele]);
};
